"use client";

import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { useAccountModal } from "@/contexts/account-modal-context";
import { useAuth } from "@/contexts/auth-context";
import { ApiError } from "@/lib/api/api-client";
import { scheduleRecital, startRecital } from "@/lib/api/recitals-api";
import { recitalStudioUrl } from "@/lib/live/live-link";
import { rememberRecitalEntry } from "@/lib/live/recital-entry";
import { randomRecitalTitle } from "@/lib/live/recital-name";

/** S'enregistrer, et où l'on en est. */
export interface QuickRecord {
  /** Crée l'enregistrement, monte sur scène et y entre. Vrai si l'on y va. */
  record(): Promise<boolean>;
  busy: boolean;
  /** Pourquoi le dernier essai n'a pas abouti, `null` s'il a abouti. */
  failure: string | null;
}

const RETRY = "Ça n'a pas pris. Réessaie.";

/**
 * « Enregistrer » : du studio au sas, en un geste.
 *
 * # Ce qu'il raccourcit
 *
 * S'enregistrer seul demandait quatre écrans : quitter le studio, ouvrir ses
 * récitals, remplir une fenêtre — un titre, « Sans public » —, puis monter sur
 * scène et revenir. Pour un geste qu'on fait à l'instant où l'envie vient, et
 * dont rien n'est à décider : un enregistrement sans public n'a ni moment, ni
 * places, ni visibilité, ni public à prévenir. Il ne reste que le titre, et il
 * se tire au sort ({@link randomRecitalTitle}) — on le renomme si la prise le
 * mérite.
 *
 * Le geste fait donc les trois appels d'affilée : créer, monter sur scène,
 * entrer. Il ne lance pas l'enregistrement — c'est le sas qui suit, avec sa
 * caméra à cadrer et son interrupteur (`recital-widgets.tsx`).
 *
 * # Sans compte
 *
 * C'est là qu'il a le plus à dire. Un enregistrement appartient à quelqu'un —
 * l'API refuserait —, donc le geste ouvre l'écran de compte, avec la promesse
 * qui va avec (« Garde ce que tu joues. », `recital_record`). Il ne se rejoue
 * pas tout seul après l'inscription : une intention gardée en mémoire le temps
 * d'un écran de compte se rejouerait un jour au mauvais moment — la modale
 * refermée puis une connexion par le header, et un enregistrement s'ouvrirait
 * tout seul.
 *
 * Il a vécu dans un bouton de la barre jusqu'au 2026-09-23 ; il est depuis la
 * première ligne du widget « Seul ou à plusieurs » (`lobby.tsx`).
 */
export function useQuickRecord(): QuickRecord {
  const { isAuthenticated } = useAuth();
  const { openAccountModal } = useAccountModal();
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const record = useCallback(async () => {
    if (!isAuthenticated) {
      openAccountModal("recital_record");

      return false;
    }

    setBusy(true);
    setFailure(null);

    try {
      let recital;

      try {
        recital = await scheduleRecital({
          title: randomRecitalTitle(),
          // L'API prend son heure à elle ; celle-ci ne sert qu'à tenir la forme.
          scheduledAt: new Date().toISOString(),
          startNow: true,
          recordingOnly: true,
        });
      } catch (cause) {
        // Le 409 est le forfait qui n'a plus de place pour un enregistrement :
        // « Réessaie » y mentirait, rien ne changera tant qu'on n'en libère
        // pas. Son message dit où.
        setFailure(cause instanceof ApiError && cause.status === 409 ? cause.message : RETRY);

        return false;
      }

      const staged = await startRecital(recital.id);

      if (staged.room === null) {
        setFailure(RETRY);

        return false;
      }

      // Le studio ne rejoint que ce que cet onglet a fait entrer : sans cette
      // note, l'adresse le renverrait à la salle d'attente qu'on vient
      // justement de lui épargner (voir `lib/live/recital-entry.ts`).
      rememberRecitalEntry(recital.id, staged.room);
      router.push(recitalStudioUrl("", recital.id));

      return true;
    } catch {
      setFailure(RETRY);

      return false;
    } finally {
      setBusy(false);
    }
  }, [isAuthenticated, openAccountModal, router]);

  return { record, busy, failure };
}
